'use client';

import { useEffect } from 'react';

// Matches failures caused by a client that outlived its deployment: route chunks
// of the previous build return 404 and stale Server Action IDs are rejected.
const STALE_CLIENT_ERROR_PATTERN = /ChunkLoadError|Loading chunk|Failed to load chunk|Failed to find Server Action/i;

const RELOADED_AT_STORAGE_KEY = 'stale-client-reloaded-at';
const RELOAD_LOOP_GUARD_MS = 30_000;

function extractMessage(candidate: unknown): string {
  if (candidate instanceof Error) {
    return `${candidate.name} ${candidate.message}`;
  }
  return typeof candidate === 'string' ? candidate : '';
}

function reloadOnceIfStale(message: string): void {
  if (!STALE_CLIENT_ERROR_PATTERN.test(message)) {
    return;
  }

  try {
    const lastReloadAt = Number(window.sessionStorage.getItem(RELOADED_AT_STORAGE_KEY) ?? 0);
    if (Date.now() - lastReloadAt < RELOAD_LOOP_GUARD_MS) {
      return;
    }
    window.sessionStorage.setItem(RELOADED_AT_STORAGE_KEY, String(Date.now()));
  } catch {
    // sessionStorage unavailable — still reload, just without loop protection
  }

  window.location.reload();
}

/**
 * Auto-recovers tabs that were open across a redeploy: reloads the page when a
 * stale chunk or Server Action failure is detected instead of leaving dead UI.
 */
export function StaleClientRecovery() {
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      reloadOnceIfStale(extractMessage(event.error) || event.message);
    };

    const handleRejection = (event: PromiseRejectionEvent) => {
      reloadOnceIfStale(extractMessage(event.reason));
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, []);

  return null;
}
